import React, { useState } from 'react';

import { Search, Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';

import useGetSubsetFacts from '@/api/queries/useGetSubsetFacts';
import { FactDropdown } from '@/new/components/fact/fact-accordion';
import { Accordion } from '@/new/components/shadcn/accordion';

interface FactBrowserProps {
  onSelectFact: (factId: string) => void;
}

const FactBrowser: React.FC<FactBrowserProps> = ({ onSelectFact }) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const [searchQuery, setSearchQuery] = useState('');

  // UUID detection regex
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const isUuid = searchQuery.trim() && uuidRegex.test(searchQuery.trim());

  // Determine if we should search by ID or text
  const effectiveSearchQuery = isUuid ? '' : searchQuery;
  const effectiveFilters = isUuid ? { factIds: [searchQuery.trim()] } : {};

  const { data: factsData, isLoading } = useGetSubsetFacts({
    matterId,
    subsetId,
    searchQuery: effectiveSearchQuery,
    filters: effectiveFilters,
  });

  const facts = factsData?.facts || [];

  return (
    <div className="flex h-full flex-col">
      {/* Search Input with Filter Button */}
      <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search facts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-gray-300 py-2 pr-3 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Facts List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-6 animate-spin text-gray-400" />
          </div>
        ) : facts.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            {searchQuery ? 'No facts found matching your search.' : 'No facts available.'}
          </div>
        ) : (
          <div className="space-y-2">
            <Accordion type="multiple" className="flex flex-col gap-2 overflow-auto">
              {facts.map((fact) => (
                <div
                  key={fact.id}
                  onClick={() => onSelectFact(fact.id)}
                  className="cursor-pointer rounded-lg border border-gray-200 transition-colors hover:border-blue-500"
                >
                  <FactDropdown fact={fact} subsetId={subsetId} editable={false} />
                </div>
              ))}
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
};

export default FactBrowser;
